import { apply, burst, celebrate, toast } from '../core/effects';
import { S } from '../core/game';
import { dayId, type AffectionKey } from '../core/state';
import { m } from '../i18n';
import { npcName } from '../i18n/labels';
import { sfx } from '../audio/sfx';
import type { Choice } from '../ui/dialog';
import { registerObjects, registerTalk } from './registry';
import { logJournal, practice } from './journal';
import { caseDoneKey, caseLive, caseOfKid, casesForWeek, KIDS, type CaseId, type KidId } from './clsPlan';
import { chatKid, kidHurt } from './conversations';
import { line, phrase, setOutcome, forget, note } from './clsTalk';
import { todayEvent, lostPencilFound, contestOffer, eventNotice } from './schoolEvents';

/**
 * Classroom cases: each Friday one or two classmates need a friend. You act as a
 * helpful peer through a short dialog; kind, honest choices teach the skill,
 * unkind ones have consequences that the kids remember.
 */

interface Ending {
  kids: readonly KidId[];
  /** Affection change for each kid in `kids`, or one value per kid. */
  love: number | Partial<Record<KidId, number>>;
  stars?: number;
  mood?: number;
  /** Memory flag per kid (replacing the other endings of this case). */
  flags: Partial<Record<KidId, string>>;
  journal: [icon: string, key: string, arg?: string];
  good: boolean;
}

/** Every outcome flag of a case, so a new ending replaces an old one. */
const CASE_FLAGS: Record<CaseId, readonly string[]> = {
  late: ['alarm', 'excuse', 'teased', 'sorry', 'said.alarm', 'said.excuse', 'said.teased', 'said.sorry'],
  homework: ['asked', 'copied', 'skipped', 'sorry', 'said.asked', 'said.copied', 'said.skipped', 'said.sorry'],
  standing: ['calm', 'clowned', 'mocked', 'sorry', 'said.calm', 'said.clowned', 'said.mocked', 'said.sorry'],
  ball: ['ballOk', 'sideMe', 'sideOther', 'grabbed', 'sorry', 'said.ballOk', 'said.sideMe', 'said.sideOther', 'said.grabbed', 'said.sorry'],
};

function finish(c: CaseId, e: Ending): void {
  const s = S();
  s.daily[caseDoneKey(c)] = true;
  const love: Partial<Record<AffectionKey, number>> = {};
  for (const k of e.kids) love[k] = typeof e.love === 'number' ? e.love : e.love[k] ?? 0;
  for (const [k, f] of Object.entries(e.flags) as [KidId, string][]) setOutcome(k, f, CASE_FLAGS[c]);
  logJournal(...e.journal);
  if (e.good) {
    celebrate(true);
    burst('heart', 8);
    const n = (s.npcTalk['cls.helped'] = (s.npcTalk['cls.helped'] ?? 0) + 1);
    if (n >= 2) note('teacher', 'helped');
  } else sfx('bad');
  apply({ stars: e.stars ?? (e.good ? 8 : 0), mood: e.mood ?? (e.good ? 8 : -4), minutes: 10, love });
}

// ---------------------------------------------------------------------------
// 小華 Xiaohua is late: apologising, time management, punctuality.
// ---------------------------------------------------------------------------
function caseLate(): void {
  const t = m().classroom.cases.late;
  const P = m().classroom.phrases;
  const end = (plan: 'alarm' | 'bag' | 'both') => {
    line('xiaohua', t.planReply, [
      {
        text: m().common.ok,
        run: () =>
          line('teacher', t.teacher, [
            {
              text: m().common.ok,
              run: () => {
                finish('late', { kids: ['xiaohua'], love: plan === 'both' ? 15 : 12, stars: plan === 'both' ? 10 : 8, flags: { xiaohua: 'alarm' }, journal: ['⏰', 'helpedLate'], good: true });
                line('xiaohua', t.thanks);
              },
            },
          ]),
      },
    ]);
  };
  const plan = () =>
    line('xiaohua', t.plan, [
      { text: t.planAlarm, run: () => end('alarm') },
      { text: t.planBag, run: () => end('bag') },
      { text: t.planBoth, run: () => end('both') },
    ], '🤔');
  const how = () =>
    line('xiaohua', t.how, [
      { text: t.howGood, run: () => phrase('xiaohua', t.phraseIntro, P.late, plan, '🙂') },
      { text: t.howQuiet, run: () => line('xiaohua', t.quietReply, [{ text: t.howGood, run: () => phrase('xiaohua', t.phraseIntro, P.late, plan, '🙂') }]) },
    ], '😟');
  line('xiaohua', t.open, [
    { text: t.good, run: how },
    {
      text: t.excuse,
      run: () => {
        finish('late', { kids: ['xiaohua'], love: -4, flags: { xiaohua: 'excuse' }, journal: ['🚌', 'lateExcuse'], good: false });
        line('narrator', t.excuseEnd);
      },
    },
    {
      text: t.tease,
      run: () => {
        finish('late', { kids: ['xiaohua'], love: -12, mood: -6, flags: { xiaohua: 'teased' }, journal: ['😶', 'teasedLate'], good: false });
        line('narrator', t.teaseEnd);
      },
    },
  ], '😰');
}

// ---------------------------------------------------------------------------
// 小美 Xiaomei didn't do her homework: asking for help honestly, "I don't know" is OK.
// ---------------------------------------------------------------------------
function caseHomework(): void {
  const t = m().classroom.cases.homework;
  const P = m().classroom.phrases;
  const teacher = () =>
    line('teacher', t.teacher, [
      {
        text: m().common.ok,
        run: () => {
          finish('homework', { kids: ['xiaomei'], love: 14, flags: { xiaomei: 'asked' }, journal: ['🙋', 'helpedHomework'], good: true });
          line('xiaomei', t.thanks);
        },
      },
    ]);
  const ask = () => phrase('xiaomei', t.phraseIntro, P.homework, teacher, '🙂');
  const scared = () =>
    line('xiaomei', t.scared, [
      { text: t.brave, run: () => line('xiaomei', t.braveReply, [{ text: m().common.ok, run: ask }]) },
      { text: t.withYou, run: () => (apply({ love: { xiaomei: 3 } }, { float: false }), line('xiaomei', t.withYouReply, [{ text: m().common.ok, run: ask }])) },
    ], '😟');
  line('xiaomei', t.open, [
    { text: t.good, run: scared },
    {
      text: t.copy,
      run: () => {
        finish('homework', { kids: ['xiaomei'], love: -5, flags: { xiaomei: 'copied' }, journal: ['📄', 'copiedHomework'], good: false });
        line('narrator', t.copyEnd);
      },
    },
    {
      text: t.sick,
      run: () => {
        finish('homework', { kids: ['xiaomei'], love: -5, flags: { xiaomei: 'skipped' }, journal: ['🤒', 'skippedHomework'], good: false });
        line('nurse', t.sickEnd);
      },
    },
  ], '😟');
}

// ---------------------------------------------------------------------------
// 阿哲 Azhe has to stand for talking in class: rules and self-control.
// ---------------------------------------------------------------------------
function caseStanding(): void {
  const t = m().classroom.cases.standing;
  const P = m().classroom.phrases;
  const teacher = () =>
    line('teacher', t.teacher, [
      {
        text: m().common.ok,
        run: () => {
          finish('standing', { kids: ['azhe'], love: 12, flags: { azhe: 'calm' }, journal: ['✋', 'helpedStanding'], good: true });
          line('azhe', t.thanks);
        },
      },
    ]);
  const say = () => phrase('azhe', t.phraseIntro, P.standing, teacher, '🙂');
  const fix = () =>
    line('azhe', t.fix, [
      { text: t.fixGood, run: say },
      { text: t.fixLater, run: () => line('azhe', t.fixLaterReply, [{ text: t.fixGood, run: say }]) },
    ], '🤔');
  const why = () =>
    line('azhe', t.why, [
      { text: t.tipWrite, run: () => line('azhe', t.tipReply, [{ text: m().common.ok, run: fix }]) },
      { text: t.tipBreathe, run: () => line('azhe', t.tipReply, [{ text: m().common.ok, run: fix }]) },
    ], '😕');
  line('azhe', t.open, [
    { text: t.good, run: why },
    {
      text: t.clown,
      run: () => {
        finish('standing', { kids: ['azhe'], love: 2, stars: -2, mood: -6, flags: { azhe: 'clowned' }, journal: ['🤪', 'clowned'], good: false });
        line('teacher', t.clownEnd);
      },
    },
    {
      text: t.mock,
      run: () => {
        finish('standing', { kids: ['azhe'], love: -12, flags: { azhe: 'mocked' }, journal: ['😶', 'mockedStanding'], good: false });
        line('azhe', t.mockEnd, undefined, '😞');
      },
    },
  ], '🧍');
}

// ---------------------------------------------------------------------------
// 小強 & 小芳 fight over a ball: I-messages, taking turns, making up.
// ---------------------------------------------------------------------------
function caseBall(): void {
  const t = m().classroom.cases.ball;
  const P = m().classroom.phrases;
  const solved = () => {
    line('narrator', t.solveReply, [
      {
        text: m().common.ok,
        run: () => {
          finish('ball', { kids: ['xiaoqiang', 'xiaofang'], love: 12, flags: { xiaoqiang: 'ballOk', xiaofang: 'ballOk' }, journal: ['🤝', 'ballMadeUp'], good: true });
          practice('sport');
          line('xiaofang', t.thanks, undefined, '😄');
        },
      },
    ], '🤝');
  };
  const solve = () =>
    line('xiaoqiang', t.solve, [
      { text: t.turns, run: solved },
      { text: t.together, run: solved },
    ], '🤔');
  const iMessage = () =>
    line('xiaofang', t.iIntro, [
      ...[
        { text: t.iGood, run: () => line('narrator', t.iReply, [{ text: m().common.ok, run: () => phrase('xiaofang', t.phraseIntro, P.ball, solve, '🙂') }]) },
        { text: t.iBad1, run: () => line('narrator', t.iBadReply, [{ text: m().classroom.ui.tryAgain, run: iMessage }], '💡') },
        { text: t.iBad2, run: () => line('narrator', t.iBadReply, [{ text: m().classroom.ui.tryAgain, run: iMessage }], '💡') },
      ].sort(() => Math.random() - 0.5),
    ], '😤');
  line('xiaoqiang', t.open, [
    { text: t.calm, run: iMessage },
    {
      text: t.sideQiang,
      run: () => {
        finish('ball', { kids: ['xiaoqiang', 'xiaofang'], love: { xiaoqiang: 3, xiaofang: -10 }, flags: { xiaoqiang: 'sideMe', xiaofang: 'sideOther' }, journal: ['⚖️', 'ballSide', 'xiaoqiang'], good: false });
        line('narrator', t.sideQiangEnd);
      },
    },
    {
      text: t.sideFang,
      run: () => {
        finish('ball', { kids: ['xiaoqiang', 'xiaofang'], love: { xiaoqiang: -10, xiaofang: 3 }, flags: { xiaoqiang: 'sideOther', xiaofang: 'sideMe' }, journal: ['⚖️', 'ballSide', 'xiaofang'], good: false });
        line('narrator', t.sideFangEnd);
      },
    },
    {
      text: t.grab,
      run: () => {
        finish('ball', { kids: ['xiaoqiang', 'xiaofang'], love: -10, flags: { xiaoqiang: 'grabbed', xiaofang: 'grabbed' }, journal: ['🏃', 'ballGrabbed'], good: false });
        line('narrator', t.grabEnd);
      },
    },
  ], '😠');
}

const CASE_FLOW: Record<CaseId, () => void> = { late: caseLate, homework: caseHomework, standing: caseStanding, ball: caseBall };

export function startCase(c: CaseId): void {
  CASE_FLOW[c]();
}

// ---------------------------------------------------------------------------
// Lost things: the lost & found box holds something a classmate lost.
// ---------------------------------------------------------------------------
const LOST: readonly { item: 'eraser' | 'bottle' | 'ruler' | 'bookmark' | 'keychain'; owner: KidId }[] = [
  { item: 'eraser', owner: 'xiaohua' },
  { item: 'bookmark', owner: 'xiaomei' },
  { item: 'keychain', owner: 'azhe' },
  { item: 'bottle', owner: 'xiaoqiang' },
  { item: 'ruler', owner: 'xiaofang' },
];
const lostToday = () => LOST[S().week % LOST.length];
const carrying = () => !!S().daily['cls.lost.taken'] && !S().daily['cls.lost.returned'];

function lostBox(): void {
  const t = m().classroom.events.lostPencil;
  if (todayEvent() === 'lostPencil' && !S().daily['cls.pencilFound']) return lostPencilFound();
  const l = lostToday();
  const what = t.items[l.item];
  if (dayId(S()) !== 'fri' || S().daily['cls.lost.taken']) return line('narrator', t.idle, undefined, '📦', m().classroom.objects.cls_lostBox);
  line('narrator', `${t.idle}\n${t.other(what, npcName(l.owner))}`, [
    { text: t.give(npcName(l.owner)), run: () => ((S().daily['cls.lost.taken'] = true), sfx('pickup'), toast(t.carrying(what, npcName(l.owner)))) },
    { text: t.leave },
  ], '📦', m().classroom.objects.cls_lostBox);
}

function giveBackChoice(id: KidId): Choice | null {
  if (!carrying() || lostToday().owner !== id) return null;
  const t = m().classroom.events.lostPencil;
  return {
    text: m().classroom.ui.giveBack(t.items[lostToday().item]),
    run: () => {
      S().daily['cls.lost.returned'] = true;
      celebrate();
      burst('heart', 6);
      apply({ stars: 4, mood: 4, love: { [id]: 10 } });
      note(id, 'returned');
      logJournal('🎁', 'returnedItem', id);
      line(id, t.thanks, undefined, '🥰');
    },
  };
}

/** "Say sorry" for a hurt they still remember: making up is always possible. */
function sorryChoice(id: KidId): Choice | null {
  const hurt = kidHurt(id);
  if (!hurt) return null;
  const t = m().classroom.kids[id];
  return {
    text: `🙏 ${t.apologise}`,
    run: () => {
      forget(id, hurt);
      note(id, 'sorry');
      // They already heard the thanks for the apology in this reply.
      note(id, 'said.sorry');
      sfx('ok');
      burst('heart', 6);
      apply({ love: { [id]: 10 }, mood: 4 });
      logJournal('🤝', 'madeUp', id);
      line(id, t.apologyReply, undefined, '🙂');
    },
  };
}

function talkKid(id: KidId): void {
  const c = caseOfKid(id);
  if (caseLive(S(), c)) return startCase(c);
  const extra = [giveBackChoice(id), sorryChoice(id)].filter((x): x is Choice => !!x);
  chatKid(id, extra);
}

// ---------------------------------------------------------------------------
// The notice board: today's notices, and the way into the spelling bee.
// ---------------------------------------------------------------------------
function noticeBoard(): void {
  const ui = m().classroom.ui;
  const s = S();
  const notes: string[] = [];
  if (dayId(s) === 'fri' && casesForWeek(s.week).some((c) => !s.daily[caseDoneKey(c)])) notes.push(ui.noticeCases);
  const ev = eventNotice();
  if (ev) notes.push(ev);
  const choices: Choice[] = [];
  const offer = contestOffer();
  if (offer) choices.push(offer);
  choices.push({ text: m().common.ok });
  line('narrator', `${ui.noticeTitle}\n\n${notes.length ? notes.join('\n\n') : ui.noticeNone}`, choices, '📌', m().classroom.objects.cls_notice);
}

function ballObject(): void {
  if (caseLive(S(), 'ball')) return startCase('ball');
  line('narrator', m().classroom.ui.ballIdle, undefined, '⚽', m().classroom.objects.cls_ball);
}

export function initClassroom(): void {
  registerTalk((id) => {
    if (!(KIDS as readonly string[]).includes(id)) return false;
    talkKid(id as KidId);
    return true;
  });
  registerObjects({ cls_lostBox: lostBox, cls_notice: noticeBoard, cls_ball: ballObject });
}

/** Debug helper: run a case regardless of the clock. */
export function debugCase(c: CaseId): void {
  S().daily[caseDoneKey(c)] = false;
  startCase(c);
}
